import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Radio, Users, Send, ShoppingBag, ArrowLeft, Loader2, Volume2 } from "lucide-react";
import { api, imgUrl, wsUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { toast } from "sonner";

export default function LiveRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const { formatMoney } = useLocale();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const chatRef = useRef(null);
  const [frame, setFrame] = useState(null);
  const [viewers, setViewers] = useState(1);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [spotlightId, setSpotlightId] = useState(null);
  const [ended, setEnded] = useState(false);
  const [soundReady, setSoundReady] = useState(false);
  const [hearing, setHearing] = useState(false);
  const soundRef = useRef({ ctx: null, next: 0, on: false });

  const { data: session } = useQuery({
    queryKey: ["live-session", id],
    queryFn: async () => (await api.get(`/live/sessions/${id}`)).data,
  });

  useEffect(() => {
    if (session) setSpotlightId(session.spotlight_id || session.product_ids?.[0]);
  }, [session]);

  useEffect(() => {
    const name = user?.name || "Guest";
    const ws = new WebSocket(wsUrl(`/api/ws/live/${id}?role=viewer&name=${encodeURIComponent(name)}`));
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === "frame") setFrame(msg.data);
      else if (msg.type === "audio") playLiveAudio(msg);
      else if (msg.type === "chat") setMessages((m) => [...m.slice(-50), msg]);
      else if (msg.type === "presence") {
        setViewers(msg.viewer_count);
        if (msg.system) setMessages((m) => [...m.slice(-50), { system: msg.system }]);
      } else if (msg.type === "spotlight") setSpotlightId(msg.product_id);
      else if (msg.type === "ended") setEnded(true);
    };
    return () => ws.close();
  }, [id, user]);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight });
  }, [messages]);

  const unlockSound = () => {
    const box = soundRef.current;
    if (!box.ctx) box.ctx = new AudioContext();
    box.on = true;
    box.ctx.resume().catch(() => {});
    setHearing(true);
  };

  const playLiveAudio = (msg) => {
    const box = soundRef.current;
    if (!box.on) {
      setSoundReady(true);
      return;
    }
    if (!box.ctx) box.ctx = new AudioContext();
    const raw = atob(String(msg.data || ""));
    if (raw.length < 2) return;
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
    const samples = new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
    const rate = Number(msg.rate) || 16000;
    const buffer = box.ctx.createBuffer(1, samples.length, rate);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) channel[i] = samples[i] / 32768;
    const source = box.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(box.ctx.destination);
    const now = box.ctx.currentTime;
    if (box.next < now) box.next = now + 0.05;
    source.start(box.next);
    box.next += buffer.duration;
  };

  const send = () => {
    const t = text.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "chat", text: t }));
    setText("");
  };

  const spotlight = session?.products?.find((p) => p.id === spotlightId) || session?.products?.[0];

  const buy = async () => {
    if (!user) return navigate("/auth?mode=login");
    if (!spotlight) return;
    try {
      await api.post("/cart", { product_id: spotlight.id, quantity: 1 });
      window.dispatchEvent(new Event("cart-updated"));
      toast.success("Added to your bag");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-16">
      <button onClick={() => navigate("/live")} className="flex items-center gap-2 text-sm text-aura-muted mb-6 hover:text-aura-ink" data-testid="live-back">
        <ArrowLeft className="w-4 h-4" /> All live rooms
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video */}
        <div className="lg:col-span-2">
          <div className="relative aspect-video bg-aura-accent rounded-xl overflow-hidden flex items-center justify-center" data-testid="live-video">
            {ended ? (
              <div className="text-white/70 text-center">
                <div className="font-serif text-2xl mb-2">This live has ended</div>
                <Link to="/live" className="link-underline text-sm">Browse other rooms</Link>
              </div>
            ) : frame ? (
              <img src={`data:image/jpeg;base64,${frame}`} alt="Live" className="w-full h-full object-cover" />
            ) : session?.products?.[0] ? (
              <>
                <img src={imgUrl(session.products[0].images?.[0])} alt="" className="w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm px-6 text-center">Waiting for the seller&apos;s camera. This is the featured product until they turn it on.</div>
              </>
            ) : (
              <div className="text-white/50 flex flex-col items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-sm">Connecting to the live stream…</span>
              </div>
            )}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="neon-live rounded-full bg-aura-accent text-white text-[10px] font-semibold tracking-luxe uppercase px-3 py-1 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white dot-blink" /> Live
              </span>
              <span className="neon-count rounded-full glass-dark text-white text-[10px] px-2.5 py-1 flex items-center gap-1.5 border" data-testid="viewer-count">
                <Users className="w-3 h-3 text-aura-cyan" /> {viewers}
              </span>
            </div>
            {soundReady && !hearing && (
              <button type="button" onClick={unlockSound} data-testid="live-hear-btn" className="absolute bottom-4 left-4 px-4 py-2 rounded-full bg-white text-aura-ink text-sm flex items-center gap-2">
                <Volume2 className="w-4 h-4" /> Tap to hear
              </button>
            )}
          </div>
          <div className="mt-4">
            <div className="text-xs text-aura-muted">{session?.seller_name}</div>
            <h1 className="font-serif text-3xl tracking-tight">{session?.title}</h1>
          </div>

          {/* Spotlight product */}
          {spotlight && (
            <div className="mt-6 bg-aura-surface border border-aura-border rounded-xl p-4 flex items-center gap-4" data-testid="spotlight-product">
              <Link to={`/product/${spotlight.id}`} className="w-20 h-24 bg-aura-sand overflow-hidden rounded shrink-0">
                <img src={imgUrl(spotlight.images?.[0])} alt={spotlight.title} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1">
                <div className="text-[10px] tracking-luxe uppercase text-aura-accent mb-1">Featured now</div>
                <div className="font-serif text-lg">{spotlight.title}</div>
                <div className="font-mono text-sm">{formatMoney(spotlight.price, spotlight.currency || "USD")}</div>
              </div>
              <button onClick={buy} data-testid="live-buy-btn" className="px-5 py-3 btn-accent text-sm font-medium flex items-center gap-2 rounded-full">
                <ShoppingBag className="w-4 h-4" /> Add
              </button>
            </div>
          )}
        </div>

        {/* Chat */}
        <div className="bg-aura-surface border border-aura-border rounded-xl flex flex-col h-[70vh]">
          <div className="px-5 py-4 border-b border-aura-border font-serif text-lg">Live chat</div>
          <div ref={chatRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="live-chat">
            {messages.map((m, i) => (
              m.system ? (
                <div key={i} className="text-xs text-aura-muted italic">{m.system}</div>
              ) : (
                <div key={i} className="text-sm"><span className="font-medium">{m.name}: </span>{m.text}</div>
              )
            ))}
          </div>
          <div className="p-3 border-t border-aura-border flex items-center gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Say something…" className="flex-1 px-3 py-2 border border-aura-border rounded-full outline-none text-sm" data-testid="live-chat-input" />
            <button onClick={send} className="w-9 h-9 rounded-full btn-accent flex items-center justify-center" data-testid="live-chat-send"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
